import React, { useState, useEffect, useRef } from "react";
import styles from "./CallSetting.module.css";
import axios from "axios";
import { API_BASE_URL, token, updateAgentFromBackend } from "../../Store/apiStore";
import { usesVobiz } from "../../utils/apiBackend";

// Ring Duration: how long a call transfer rings the team's phone before the
// agent takes the caller back. Same 10–60s window the backend enforces.
const RING_MIN = 10;
const RING_MAX = 60;

// What is left on the team's voicemail. Same defaults the backend falls back
// to, so an empty box and an untouched one behave the same.
const VM_DEFAULTS = {
  static:
    "Hi, this is {{agent_name}} from {{business_name}}. {{caller_name}} just called about {{reason}} and could not be connected. Please call them back at {{caller_number}}.",
  prompt:
    "Leave a short voicemail for the team saying who called, why they called, and the number to call them back on.",
};
const VM_VARIABLES = ["caller_name", "caller_number", "reason", "department", "agent_name", "business_name"];

// Python receptionists only — Retell agents keep their transfer settings in
// Retell, and a Pounce agent dials leads over Twilio, which has no transfer.
// Voicemail Detection is hidden on Vobiz (India) numbers: Vobiz has no
// answer-machine check, so the toggle would do nothing there.
export default function TransferSetting() {
  const [isPython, setIsPython] = useState(false);
  const [ringSecs, setRingSecs] = useState(usesVobiz() ? 15 : 25);
  const [voicemailDetection, setVoicemailDetection] = useState(false);
  const [vmAction, setVmAction] = useState("hangup");
  const [vmMode, setVmMode] = useState("static");
  const [vmText, setVmText] = useState(VM_DEFAULTS.static);
  const [hovered, setHovered] = useState(false);

  // Same id the rest of Call Setting fetches with.
  const resolvedAgentId = sessionStorage.getItem("SelectAgentId");

  useEffect(() => {
    if (!resolvedAgentId) return;
    const fetchAgentDetails = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/agent/getAgent/${resolvedAgentId}`, {
          headers: {
            "Content-Type": "application/json",
            // The current token, not apiStore's copy from page load — that one
            // is empty for a tab opened before login, and the 401 hid this card.
            Authorization: `Bearer ${localStorage.getItem("token") || token}`,
          },
        });
        const {
          agentCreationType,
          agentRole,
          transfer_ring_secs,
          transfer_voicemail_detection,
          transfer_voicemail_action,
          transfer_voicemail_mode,
          transfer_voicemail_text,
        } = res.data;
        setIsPython(
          String(agentCreationType || "").toLowerCase() === "python" &&
            String(agentRole || "").toLowerCase() !== "pounce"
        );
        if (transfer_ring_secs) {
          setRingSecs(Math.min(Math.max(transfer_ring_secs, RING_MIN), RING_MAX));
        }
        if (transfer_voicemail_detection != null) {
          setVoicemailDetection(Boolean(transfer_voicemail_detection));
        }
        const mode = transfer_voicemail_mode === "prompt" ? "prompt" : "static";
        setVmAction(transfer_voicemail_action === "message" ? "message" : "hangup");
        setVmMode(mode);
        setVmText(transfer_voicemail_text || VM_DEFAULTS[mode]);
      } catch (error) {
        console.error("Error fetching agent details:", error);
      }
    };
    fetchAgentDetails();
  }, [resolvedAgentId]);

  // DEBOUNCED like the other sliders, and never disabled mid-drag.
  const commitTimers = useRef({});
  const commit = (field, value) => {
    if (!resolvedAgentId) return;
    clearTimeout(commitTimers.current[field]);
    commitTimers.current[field] = setTimeout(async () => {
      try {
        await updateAgentFromBackend({ agent_id: resolvedAgentId, [field]: value });
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
      }
    }, 400);
  };

  const toggleVoicemail = () => {
    const next = !voicemailDetection;
    setVoicemailDetection(next);
    commit("transfer_voicemail_detection", next);
  };

  const chooseAction = (action) => {
    setVmAction(action);
    commit("transfer_voicemail_action", action);
    // Save the sentence the owner sees, so a never-edited default still goes out.
    if (action === "message") commit("transfer_voicemail_text", vmText);
  };

  const chooseMode = (mode) => {
    if (mode === vmMode) return;
    setVmMode(mode);
    commit("transfer_voicemail_mode", mode);
    // Swap in the other default unless the owner wrote their own text.
    if (!vmText.trim() || vmText === VM_DEFAULTS[vmMode]) {
      setVmText(VM_DEFAULTS[mode]);
      commit("transfer_voicemail_text", VM_DEFAULTS[mode]);
    }
  };

  const changeText = (text) => {
    setVmText(text);
    commit("transfer_voicemail_text", text);
  };

  if (!isPython) return null;

  const fill = ((ringSecs - RING_MIN) / (RING_MAX - RING_MIN)) * 100;

  return (
    <div className={styles.settingCard}>
      <p className={styles.settingCardTitle}>Call Transfer</p>
      <div className={styles.settingCardBody}>
        <div
          className={styles.sliderContainer}
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => setHovered(false)}
        >
          <div className={styles.toggleRow}>
            <label className={styles.label}>Ring Duration</label>
          </div>
          <p className={styles.description}>
            How long a transfer rings your team before the agent takes the caller back.
          </p>
          <div className={styles.sliderWrapper}>
            <input
              type="range"
              min={RING_MIN}
              max={RING_MAX}
              step="1"
              value={ringSecs}
              onChange={(e) => setRingSecs(parseInt(e.target.value, 10))}
              onMouseUp={(e) => commit("transfer_ring_secs", parseInt(e.target.value, 10))}
              onTouchEnd={(e) => commit("transfer_ring_secs", parseInt(e.target.value, 10))}
              className={styles.sliderRange}
              style={{
                background: `linear-gradient(to right, #693bff ${fill}%, #fff ${fill}%)`,
                cursor: "pointer",
              }}
            />
            <span className={styles.sliderValue}>{ringSecs} s</span>
          </div>
          <div className={`${styles.minMaxRow} ${hovered ? styles.minMaxRowVisible : ""}`}>
            <span>Short</span>
            <span>Long</span>
          </div>
        </div>

        {!usesVobiz() && (
          <>
            <div className={` ${styles.toggleRow} ${styles.toggleRow1} `}>
              <label className={styles.label}>Voicemail Detection</label>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={voicemailDetection}
                  onChange={toggleVoicemail}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
            <p className={styles.description}>
              If voicemail answers a transfer, don't connect the caller to it — the agent
              takes a message instead.
            </p>

            {voicemailDetection && (
              <div className={styles.vmBox}>
                <p className={styles.vmTitle}>Voicemail Response</p>
                <label className={styles.vmRadio}>
                  <input
                    type="radio"
                    name="vmAction"
                    checked={vmAction === "hangup"}
                    onChange={() => chooseAction("hangup")}
                  />
                  <span>Hang up if reaching voicemail</span>
                </label>
                <label className={styles.vmRadio}>
                  <input
                    type="radio"
                    name="vmAction"
                    checked={vmAction === "message"}
                    onChange={() => chooseAction("message")}
                  />
                  <span>Leave a message if reaching voicemail</span>
                </label>

                {vmAction === "message" && (
                  <>
                    <div className={styles.vmTabs}>
                      {[
                        ["prompt", "Prompt"],
                        ["static", "Static Sentence"],
                      ].map(([mode, label]) => (
                        <button
                          key={mode}
                          type="button"
                          className={`${styles.vmTab} ${vmMode === mode ? styles.vmTabActive : ""}`}
                          onClick={() => chooseMode(mode)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <textarea
                      className={styles.vmText}
                      rows={4}
                      maxLength={1000}
                      value={vmText}
                      onChange={(e) => changeText(e.target.value)}
                    />
                    <p className={styles.vmHint}>
                      {vmMode === "prompt"
                        ? "The agent writes the message from this instruction and the call so far."
                        : "Spoken after the beep. Variables: " +
                          VM_VARIABLES.map((v) => `{{${v}}}`).join(", ")}
                    </p>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
